import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";

import type { Insole } from "~/api/insoles";
import { updateInsoleSettings } from "~/api/insoles";
import { errorToast, useToastStore } from "~/components/Shared/Toast";
import { Button, Select } from "~/ui";

const AutomaticRecording = ({
  settings,
  patientId,
  insoleId,
}: {
  settings: Insole["settings"];
  patientId: number;
  insoleId: number;
}) => {
  const [automaticRecording, setAutomaticRecording] = useState(
    settings.auto_record,
  );
  const [isEdit, setIsEdit] = useState(false);
  const queryClient = useQueryClient();

  const { pushToast } = useToastStore();

  const { mutate: updateInsoleSettingsMutation } = useMutation({
    mutationFn: (autoRecord: string) =>
      updateInsoleSettings(insoleId, autoRecord),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getPatient", { id: patientId }],
      });
      void pushToast({
        type: "success",
        message: "Changed automatic recording settings successfully!",
      });
    },
    onError: (err) => {
      errorToast(err);
    },
  });

  return (
    <div className="flex justify-between border-b border-neutral-10 pb-2 pt-6">
      <div className="flex items-center gap-4">
        <p className="text-lg font-medium text-secondary-20 dark:text-secondary">
          Automatic Recording:{" "}
        </p>
        <Select
          value={automaticRecording}
          onChange={(e) => {
            setAutomaticRecording(e.target.value);
            setIsEdit(true);
          }}
          className="h-auto w-32 py-1"
        >
          <option value="off">Off</option>
          <option value="1x">1x/day</option>
          <option value="3x">3x/day</option>
          <option value="hourly">Every hour</option>
        </Select>
        <Button
          text={"Save"}
          color="primary"
          onClick={() => {
            updateInsoleSettingsMutation(automaticRecording);
            setIsEdit(false);
          }}
          className={clsx(
            "h-max w-max rounded-xl px-4 py-1",
            isEdit ? "block" : "hidden",
          )}
        />
      </div>

      {/* Sync Status Indicator */}
      <div className="flex items-center gap-2">
        <div
          className={`h-3 w-3 rounded-full ${
            settings.synced
              ? "bg-success-40" // Green circle for synced
              : "bg-error-30" // Red circle for not synced
          }`}
        />
        <p className="text-secondary-20 dark:text-secondary">
          {settings.synced ? "Synced" : "Not Synced"}
        </p>
      </div>
    </div>
  );
};

export default AutomaticRecording;
